/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * NotificationPrimerScreen — pre-permission "priming" screen shown right after
 * Welcome, before phone entry.
 *
 * WHY a priming screen (vs. just firing the iOS dialog): iOS shows the system
 * "Allow Notifications?" alert only ONCE per install. If it fires cold with no
 * context the user often taps "Don't Allow", and there's no second chance in
 * the app. So we explain the value FIRST (safety alerts, SOS, messages that
 * reach them even when the app is closed), then "Turn on" triggers the real
 * dialog at a moment the user understands.
 *
 * The user is NOT signed in yet at this point, so this screen only requests
 * device-level PERMISSION — it does not (and cannot) store an FCM token. The
 * token is captured post-auth by useFCMTokenCapture once a uid exists. Granting
 * here also primes APNs early, which helps native phone-auth on the next step.
 *
 * Never blocks onboarding: both "Turn on" (whatever the user chooses in the iOS
 * dialog) and "Not now" advance. A denied user can still re-enable later in iOS
 * Settings; we don't trap them here.
 */

import { useState } from "react";
import { BellRing, ShieldAlert, MessageCircle, MapPin } from "lucide-react";
import { requestNotificationPermission } from "@/utils/notificationPermission";
import { primeApns } from "@/hooks/useApnsRegistration";
import OnboardingShell from "@/components/onboarding/OnboardingShell";

interface Props {
  /** Advance to the next onboarding step (phone entry). Called after the user
   *  responds to the prompt OR taps "Not now" — onboarding never blocks here. */
  onContinue: () => void;
  /** Optional back affordance (returns to Welcome). */
  onBack?: () => void;
}

const REASONS: { Icon: typeof BellRing; text: string }[] = [
  { Icon: ShieldAlert, text: "Safety alerts and SOS from your circle" },
  { Icon: MessageCircle, text: "Messages and friend requests" },
  { Icon: MapPin, text: "Walk Me Home and check-in reminders" },
];

export default function NotificationPrimerScreen({ onContinue, onBack }: Props) {
  const [requesting, setRequesting] = useState(false);

  const handleTurnOn = async () => {
    setRequesting(true);
    try {
      // Fires the native iOS "Allow Notifications?" dialog (or the Android /
      // web equivalent). This is the SINGLE notification ask in the phone-first
      // flow — useApnsRegistration only checks permission, never requests it.
      // Resolves once the dialog closes. We advance regardless of grant/deny —
      // and even if it throws — so onboarding is never trapped.
      const result = await requestNotificationPermission();
      if (result.state === "granted") {
        // Kick native APNs registration now so FIRAuth holds the token before
        // the OTP send a few screens later (silent-push path, no reCAPTCHA).
        // Fire-and-forget — don't block the transition to phone entry.
        void primeApns();
      }
    } catch {
      // Permission flow must never block account creation.
    } finally {
      setRequesting(false);
      onContinue();
    }
  };

  return (
    <OnboardingShell
      step={1}
      totalSteps={5}
      onBack={onBack}
      ariaLabel="Turn on notifications"
      ctaLabel="Turn on notifications"
      ctaBusy={requesting}
      onCta={handleTurnOn}
      secondaryLabel={requesting ? undefined : "Not now"}
      onSecondary={onContinue}
    >
      <div className="mx-auto flex max-w-md flex-col items-center text-center">
        <div className="mt-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#7C52F5]/12 text-[#7C52F5] shadow-[0_12px_30px_-16px_rgba(124,82,245,0.7)]">
          <BellRing size={30} aria-hidden="true" />
        </div>
        <h1 className="mt-5 text-2xl font-bold tracking-tight text-[#2A2342]">
          Stay in the loop
        </h1>
        <p className="mt-2.5 text-[15px] leading-relaxed text-[#6B5FA0]">
          MyAmbits keeps you connected to your circle — even when the app is
          closed. Turn on notifications so you never miss something that matters.
        </p>

        {/* Concrete reasons — make the value tangible before the iOS dialog. */}
        <ul className="mt-7 w-full space-y-3 text-left">
          {REASONS.map(({ Icon, text }) => (
            <li key={text} className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#7C52F5]/10 text-[#7C52F5]">
                <Icon size={20} aria-hidden="true" />
              </span>
              <span className="text-[15px] font-medium text-[#2A2342]">
                {text}
              </span>
            </li>
          ))}
        </ul>

        <p className="mt-6 text-xs leading-relaxed text-[#A89FC4]">
          You can change this anytime in Settings.
        </p>
      </div>
    </OnboardingShell>
  );
}
